"use client";

import { useState, type FormEvent } from "react";
import { CheckCircle2, Mail, Send, ShieldAlert } from "lucide-react";
import { Button } from "@carpybara/ui/shadcn/button";
import {
  Field as BaseField,
  FieldLabel,
} from "@carpybara/ui/shadcn/field";
import { Input } from "@carpybara/ui/shadcn/input";
import { Textarea } from "@carpybara/ui/shadcn/textarea";

export interface ContactFormLabels {
  heading: string;
  intro: string;
  labelName: string;
  labelEmail: string;
  labelCompany: string;
  labelCompanyHint: string;
  labelSources: string;
  labelSourcesHint: string;
  labelUseCase: string;
  labelUseCaseHint: string;
  submit: string;
  submitting: string;
  successHeading: string;
  successBody: string;
  errorGeneric: string;
  errorEmail: string;
  errorUseCase: string;
}

interface Props {
  labels: ContactFormLabels;
  /** Pre-fill from server-side session when available. */
  defaultName?: string;
  defaultEmail?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactForm({ labels, defaultName = "", defaultEmail = "" }: Props) {
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [company, setCompany] = useState("");
  const [sources, setSources] = useState("");
  const [useCase, setUseCase] = useState("");
  // Honeypot — bots fill it, humans don't see it.
  const [website, setWebsite] = useState("");

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!EMAIL_RE.test(email.trim())) {
      setError(labels.errorEmail);
      return;
    }
    if (useCase.trim().length === 0) {
      setError(labels.errorUseCase);
      return;
    }

    setPending(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          company: company.trim(),
          sourcesOfInterest: sources.trim(),
          useCase: useCase.trim(),
          website,
        }),
      });
      const payload = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };
      if (!res.ok || !payload.ok) {
        setError(payload.message ?? labels.errorGeneric);
        setPending(false);
        return;
      }
      setSubmitted(true);
    } catch {
      setError(labels.errorGeneric);
      setPending(false);
    }
  };

  if (submitted) {
    return (
      <div
        id="contact"
        className="rounded-2xl border-2 border-green-500/40 bg-(--bg-card) p-8 text-center"
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-green-400" />
        <h2 className="mt-4 text-2xl font-bold text-(--text-primary)">
          {labels.successHeading}
        </h2>
        <p className="mx-auto mt-3 max-w-lg text-sm text-(--text-secondary)">
          {labels.successBody}
        </p>
      </div>
    );
  }

  return (
    <form
      id="contact"
      onSubmit={onSubmit}
      className="rounded-2xl border border-(--border) bg-(--bg-card) p-6 sm:p-8 space-y-5"
    >
      <header className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-(--accent-bg) text-(--accent)">
          <Mail className="h-5 w-5" />
        </span>
        <div className="space-y-1">
          <h2 className="text-xl font-bold text-(--text-primary)">{labels.heading}</h2>
          <p className="text-sm text-(--text-muted)">{labels.intro}</p>
        </div>
      </header>

      {/* Honeypot — invisible to humans, irresistible to bots */}
      <div className="hidden" aria-hidden>
        <label>
          Website
          <Input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={labels.labelName}>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={pending}
            maxLength={120}
          />
        </Field>
        <Field label={labels.labelEmail} required>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
            required
            maxLength={254}
          />
        </Field>
      </div>

      <Field label={labels.labelCompany} hint={labels.labelCompanyHint}>
        <Input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          disabled={pending}
          maxLength={160}
        />
      </Field>

      <Field label={labels.labelSources} hint={labels.labelSourcesHint}>
        <Input
          type="text"
          value={sources}
          onChange={(e) => setSources(e.target.value)}
          disabled={pending}
          maxLength={200}
        />
      </Field>

      <Field label={labels.labelUseCase} hint={labels.labelUseCaseHint} required>
        <Textarea
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
          disabled={pending}
          required
          maxLength={2000}
          rows={4}
        />
      </Field>

      {error && (
        <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3.5 py-2.5 text-sm text-red-300">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Button
        type="submit"
        fullWidth
        loading={pending}
        iconStart={pending ? undefined : <Send className="h-4 w-4" />}
      >
        {pending ? labels.submitting : labels.submit}
      </Button>
    </form>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <BaseField>
      <FieldLabel required={required}>
        {label}
        {hint && (
          <span className="text-[10px] font-normal normal-case tracking-normal text-(--text-faint)">
            ({hint})
          </span>
        )}
      </FieldLabel>
      {children}
    </BaseField>
  );
}
